import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { ConfidenceMeter } from '../components/ConfidenceMeter';
import { toast } from 'sonner';
import {
  ArrowLeft, ShoppingCart, Target, ShieldAlert, TrendingUp,
  AlertTriangle, Clock, Search, Brain, Newspaper, Building,
  Users, MessageSquare, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const agentConfig = {
  market_tracker: { icon: Search, name: 'Market Tracker', desc: 'Overall market conditions and trends' },
  sentiment: { icon: Brain, name: 'Market Sentiment', desc: 'Sentiment analysis and fear/greed indicators' },
  company_news: { icon: Newspaper, name: 'Company News', desc: 'Recent news catalysts and risks' },
  company_info: { icon: Building, name: 'Company Intel', desc: 'Fundamentals and competitive position' },
  social_sentiment: { icon: Users, name: 'Social Sentiment', desc: 'Social media and retail investor signals' },
  conversation_analyzer: { icon: MessageSquare, name: 'Deep Analyzer', desc: 'Institutional flow and hidden patterns' },
};

export default function RecommendationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRec = async () => {
      try {
        const res = await fetch(`${API}/api/recommendations/${id}`);
        if (res.ok) {
          setRec(await res.json());
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRec();
  }, [id]);

  const handleBuy = async () => {
    try {
      const res = await fetch(`${API}/api/recommendations/${id}/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: 1 }),
      });
      if (res.ok) {
        toast.success(`Bought ${rec.symbol} at $${rec.buy_price}`);
        setRec(prev => ({ ...prev, status: 'bought' }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to buy');
      }
    } catch {
      toast.error('Network error');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-32 rounded bg-[hsl(var(--surface-2))]" />
        <Skeleton className="h-64 w-full rounded-xl bg-[hsl(var(--surface-2))]" />
        <Skeleton className="h-96 w-full rounded-xl bg-[hsl(var(--surface-2))]" />
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="text-center py-20">
        <p className="text-[hsl(var(--muted-foreground))]">Recommendation not found</p>
        <Button variant="ghost" onClick={() => navigate('/')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  const isActive = rec.status === 'active';
  const estReturn = rec.estimated_return_pct || (
    rec.buy_price > 0 ? ((rec.target_price - rec.buy_price) / rec.buy_price * 100).toFixed(1) : 0
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recommendation-detail-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/')} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
      </Button>

      {/* Hero Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-3xl font-bold font-mono tracking-wide">{rec.symbol}</h1>
                    <Badge variant="outline" className={`uppercase font-semibold ${
                      isActive ? 'text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30' :
                      rec.status === 'bought' ? 'text-emerald-400 border-emerald-500/30' :
                      'text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]'
                    }`}>
                      {rec.status}
                    </Badge>
                    <Badge variant="outline" className="text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30">
                      {rec.action}
                    </Badge>
                  </div>
                  <p className="text-lg text-[hsl(var(--muted-foreground))]">{rec.company_name}</p>
                </div>
                <Badge variant="outline" className={`text-sm ${
                  rec.risk_level === 'LOW' ? 'text-emerald-400 border-emerald-500/30' :
                  rec.risk_level === 'HIGH' ? 'text-red-400 border-red-500/30' :
                  'text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30'
                }`}>
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  {rec.risk_level} Risk
                </Badge>
              </div>

              {/* Price Grid */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <PriceBox icon={ShoppingCart} label="Buy Price" value={rec.buy_price} color="default" />
                <PriceBox icon={Target} label="Target Price" value={rec.target_price} color="success" />
                <PriceBox icon={ShieldAlert} label="Stop Loss" value={rec.stop_loss} color="danger" />
              </div>

              {/* Confidence + Return */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <ConfidenceMeter value={rec.confidence || 0} />
                </div>
                <div className="flex flex-col items-end justify-center">
                  <span className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Estimated Return</span>
                  <span className="font-mono text-3xl font-bold tabular-nums text-emerald-400">
                    +{Number(estReturn).toFixed(1)}%
                  </span>
                  {rec.timeframe && (
                    <span className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      <Clock className="w-3 h-3" /> {rec.timeframe}
                    </span>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="p-4 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] mb-6">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" />
                  AI Analysis Summary
                </h3>
                <p className="text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">{rec.summary}</p>
              </div>

              {rec.market_summary && (
                <div className="p-4 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[hsl(var(--info))]" />
                    Market Context
                  </h3>
                  <p className="text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">{rec.market_summary}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Order Plan */}
        <div>
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] sticky top-24">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Order Plan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <OrderRow label="Buy At" value={`$${rec.buy_price?.toFixed(2)}`} />
                <OrderRow label="Target" value={`$${rec.target_price?.toFixed(2)}`} className="text-emerald-400" />
                <OrderRow label="Stop Loss" value={`$${rec.stop_loss?.toFixed(2)}`} className="text-red-400" />
                <Separator className="bg-[hsl(var(--border))]" />
                <OrderRow label="Est. Return" value={`+${Number(estReturn).toFixed(1)}%`} className="text-emerald-400 font-bold" />
                <OrderRow label="Timeframe" value={rec.timeframe || 'N/A'} />
                <OrderRow label="Confidence" value={`${rec.confidence}%`} className="text-[hsl(var(--primary))]" />
              </div>

              {isActive && (
                <Button
                  onClick={handleBuy}
                  className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 font-semibold"
                  data-testid="detail-buy-button"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Mark as Bought
                </Button>
              )}

              <div className="text-xs text-[hsl(var(--muted-foreground))] space-y-1">
                <p>Auto-expire: When price exceeds buy price</p>
                {rec.created_at && <p>Created: {new Date(rec.created_at).toLocaleString()}</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Agent Analyses */}
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Agent Analysis Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="w-full" data-testid="recommendation-detail-open-agent-accordion">
            {Object.entries(agentConfig).map(([key, config]) => {
              const Icon = config.icon;
              const rationale = rec.agent_rationales?.[key] || 'No analysis available';
              return (
                <AccordionItem key={key} value={key} className="border-[hsl(var(--border))]">
                  <AccordionTrigger className="hover:no-underline px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[hsl(var(--surface-2))] flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[hsl(var(--primary))]" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium">{config.name}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{config.desc}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="ml-11 p-3 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
                      <p className="text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">{rationale}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}

function PriceBox({ icon: Icon, label, value, color }) {
  const colorMap = {
    success: 'text-emerald-400',
    danger: 'text-red-400',
    default: 'text-[hsl(var(--foreground))]',
  };
  return (
    <div className="p-4 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
      <div className={`flex items-center gap-1 text-xs mb-2 ${color !== 'default' ? colorMap[color] : 'text-[hsl(var(--muted-foreground))]'}`}>
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <p className={`font-mono text-xl font-bold tabular-nums ${colorMap[color]}`}>
        ${value?.toFixed(2)}
      </p>
    </div>
  );
}

function OrderRow({ label, value, className = '' }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[hsl(var(--muted-foreground))]">{label}</span>
      <span className={`font-mono text-sm tabular-nums font-medium ${className}`}>{value}</span>
    </div>
  );
}
